<template>
  <div
    class="fixed-footer"
  >
    <BulkDiscount />
    <Transition
      name="expand-fade"
      @before-enter="(el) => el.style.height = '0'"
      @enter="(el) => el.style.height = el.scrollHeight + 'px'"
      @after-enter="(el) => el.style.height = ''"
      @before-leave="(el) => el.style.height = el.scrollHeight + 'px'"
      @leave="(el) => el.style.height = '0'"
    >
      <div
        v-if="isExpanded"
        class="expand-content"
      >
        <div class="item">
          <div class="label">
            Total Items({{ estimatedQuantity }})
          </div>
          <div class="value">
            <IntlCurrency :currency="currencyData.targetCurrency" />
            {{ totalItemsAmount.toFixed(2) }}
          </div>
        </div>
        <div
          class="item"
          style="margin-bottom: 0;"
        >
          <div class="label">
            {{ shippingConfirmed ? 'Shipping Cost' : 'Est.Shipping' }}
          </div>
          <div class="value">
            <template v-if="!shippingConfirmed">
              Need a Quote
            </template>
            <template v-else>
              <IntlCurrency
                :currency="currencyData.targetCurrency"
              />
              {{ shipAmount.toFixed(2) }}
            </template>
          </div>
        </div>
      </div>
    </Transition>
    <div class="summary-info">
      <div class="summary-l">
        <div class="item">
          <div class="label total-cost">
            {{ shippingConfirmed ? 'Total Cost' : 'Est.Total Cost' }}
          </div>
          <div
            class="value total-cost"
            style="color: #052784; padding-right: 4px; font-size: 18px;"
          >
            <IntlCurrency :currency="currencyData.targetCurrency" />
            {{ totalAmount.toFixed(2) }}
          </div>
        </div>
        <div class="item">
          <div class="label pifp">
            {{ shippingConfirmed ? 'Per-Item Fulfillment Price' : 'Est.Per-Item Fulfillment Price' }}
          </div>
          <div class="value pifp">
            <IntlCurrency :currency="currencyData.targetCurrency" />
            {{ pifpPrice }}
          </div>
        </div>
      </div>
      <Icon
        @click="isExpanded = !isExpanded"
        :class="{ 'expanded': isExpanded }"
        class="expand-icon"
        name="arrow-down-l-2"
      />
    </div>
    <Footer
      ref="footerRef"
      @add-cart="handleAddToCart"
      :favorite="data?.collect"
    />
    <Processing
      v-model="processingVisible"
      :expand="isExpanded"
    />
  </div>

  <Popup
    v-model:show="showAddressInfo"
    teleport="body"
    position="bottom"
    height="100%"
  >
    <div class="pop-address-container">
      <div class="pop-container-header">
        <img
          src="@/assets/H5/orderFilling/h5-back.svg"
          alt=""
          class="pop-container-header-back"
          @click="handleCloseAddressInfo(false)"
        >
        <div class="pop-container-header-title">
          Shipping Address
        </div>
        <img
          src="@/assets/H5/offer/sourcing-close.svg"
          alt=""
          class="pop-container-header-close"
          @click="handleCloseAddressInfo(false)"
        >
      </div>
      <H5AddressInfo
        :handle-close="handleCloseAddressInfo"
        :address-type="2"
      />
    </div>
  </Popup>

  <ExceedQuantityDialog
    v-if="exceedOrderQuantityDialog"
    @close="exceedOrderQuantityDialog = false"
    :quantity="data?.minOrderQuantity"
  />

  <div
    class="flying-image"
    ref="flyImgRef"
  >
    <img
      v-if="addImageUrl"
      class="flying-image__img"
      :src="addImageUrl"
      alt=""
    >
    <img
      v-else
      class="flying-image__img"
      src="@/assets/empty-image.svg"
      alt=""
    >
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Icon } from '@/components/Icon'
import { useCartStore } from '@/pinia/cart'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { useCurrencyStore } from '@/pinia/currency'
import { useUserStore } from '@/pinia/user'
import { useLoginModal } from '@/components/Login'
import { buriedPointUtil, gaAnalytics, metaPixelTrack } from '@/utils/utils'
import { useAddressStore } from '@/pinia/address'
import { fetchData } from '@/utils/fetch'
import { Popup } from '@/components/Popup'
import { usePlugin } from '@/hooks/usePlugin'
import H5AddressInfo from '@/components/H5/H5AddressInfo/index.vue'
import Processing from './Processing.vue'
import ExceedQuantityDialog from '@/components/H5/ExceedQuantityDialog/index.vue'
import Footer from '../Footer.vue'
import { useAddAnimation } from '../../hooks/useAddAnimation'
import { useCalcShip } from '../../../hooks/useCalcShip'
import BulkDiscount from './BulkDiscount.vue'
import { useProductDetail } from '../../../hooks/useProductDetail'

const emits = defineEmits(['deliveryTime', 'close', 'addCartSuccess'])

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  isProcessing: {
    type: Boolean,
    default: false
  }
})

const { flyImgRef, addImageUrl, footerRef, addAnimation } = useAddAnimation()

const { $toast } = usePlugin()

const addressStore = useAddressStore()

const cartStore = useCartStore()

const userStore = useUserStore()

const currencyStore = useCurrencyStore()

const { open: openLoginModal } = useLoginModal()

const isExpanded = ref(false)

const showAddressInfo = ref(false)

const processingVisible = ref(props.isProcessing)

const exceedOrderQuantityDialog = ref(false)

const currencyData = computed(() => currencyStore.current)

const { detailInfo } = useProductDetail()

const { selectedSkuList, shippingConfirmed, shipAmount, totalItemsAmount, totalAmount } = useCalcShip()

const estimatedQuantity = computed(() => {
  if (selectedSkuList.value.length === 0) return 0
  return selectedSkuList.value.reduce((pre, cur) => {
    return pre + cur.quantity
  }, 0)
})

const pifpPrice = computed(() => {
  if (!totalAmount.value) return 0

  const val = (totalItemsAmount.value + shipAmount.value) / estimatedQuantity.value
  return val.toFixed(2)
})

watch(() => props.isProcessing, (value) => {
  processingVisible.value = value
})

const handleCloseAddressInfo = (saved) => {
  showAddressInfo.value = false
  if (saved) {
    $toast('Address added successfully', { type: 'success' })
  }
}

const handleAddToCart = async () => {
  if (!userStore.userInfo.tk) {
    openLoginModal()
    buriedPointUtil({ eventId: 'MPLG501' })
    emits('close')
    return
  }

  if (!addressStore.cacheAddress) {
    showAddressInfo.value = true
    return
  }

  if (!estimatedQuantity.value) {
    $toast('Quantity cannot be empty.', { type: 'error' })
    return
  }

  const minOrderQuantity = props.data.minOrderQuantity
  if (minOrderQuantity > estimatedQuantity.value) {
    $toast('The minimum order quantity for the product has not been reached. Please continue to add products.', {
      type: 'error'
    })
    return
  }

  const firstSelectedSkuUrl = selectedSkuList.value.find(item => item.skuImageUrl != null)?.skuImageUrl

  addAnimation(firstSelectedSkuUrl)
  /**
   * Meta Pixel 埋点：点击加入购物车
   */
  metaPixelTrack('PAC201', 'AddToCart', {
    content_ids: [props.data.offerId],
    content_type: 'product',
    currency: '$',
    value: 2
  })

  const { code, msg } = await fetchData('/appb/b2b/cart/cartAddV2', {
    method: 'post',
    body: JSON.stringify({
      currency: currencyData.value.targetCurrency,
      currencyUnit: currencyData.value.targetUnit,
      details: selectedSkuList.value.map(item => ({ spuId: detailInfo.value.offerId, ...item }))
    })
  })
  if (code === 200) {
    $toast('Added successfully', { type: 'success' })
    gaAnalytics('mp_add_to_cart_success', 'mp_add_to_cart_success')
    buriedPointUtil({ eventId: 'MPPD601' })
    cartStore.updateCartCount()
    emits('addCartSuccess')
  } else if ([5216, 5217, 500001].includes(code)) {
    $toast(msg, { type: 'error' })
  } else if (code === 200001) {
    $toast('Please fill delivery address.')
    localStorage.removeItem('addressForm')
    showAddressInfo.value = true
  } else if (code === 100001) {
    exceedOrderQuantityDialog.value = true
  } else {
    $toast('Add to cart failed', { type: 'error' })
  }
}
</script>

<style lang='less' scoped>
.fixed-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  backdrop-filter: blur(10px);
  border-top: 1px solid #EBF1F5;
  box-sizing: border-box;
  z-index: 99;

  .expand-content {
    background-color: white;
    padding: 12px 28px 0 12px;
    overflow: hidden;
  }

  .summary-info {
    position: relative;
    display: flex;
    margin-top: 12px;
    padding: 0 12px;
    z-index: 9;

    .summary-l {
      flex: 1;
    }

    .expand-icon {
      position: relative;
      top: 3px;
      transition: transform .3s;
      &.expanded {
        transform: rotate(180deg);
      }
    }
  }

  .summary {
    position: relative;
    display: flex;
    margin-top: 16px;
    padding: 0 12px;

    .summary-l {
      flex: 1;
    }

    .expand-icon {
      position: relative;
      top: 0;
      right: 0;
      transition: all 0.3s;
      &.expanded {
        transform: rotate(180deg);
      }
    }
  }

  .item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    .label {
      color: #232323;
      font-size: 12px;
      font-family: mon-500;
      &.total-cost {
        font-size: 14px;
        font-family: mon-700;
      }
      &.pifp {
        color: #168A73;
        font-size: 11px;
        font-family: mon-600;
      }
    }
    .value {
      display: flex;
      align-items: center;
      color: #232323;
      font-size: 12px;
      font-family: mon-600;
      &.total-cost {
        font-size: 14px;
        font-family: mon-700;
      }
      &.pifp {
        color: #168A73;
        font-size: 11px;
        font-family: mon-700;
      }
    }
  }
}

.expand-fade-enter-active,
.expand-fade-leave-active {
  transition: height 0.2s ease-in-out;
}

.pop-address-container {
  padding: 0 12px;
  .pop-container-header {
    height: 48px;
    border-radius: 8px;
    background-color: #FFFFFF;
    margin-bottom: 12px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    .pop-container-header-title {
      font-family: mon-700;
      font-size: 14px;
      color: #232323;
      line-height: 18px;
    }
  }
}

.flying-image {
  width: 150px;
  height: 150px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1);
  opacity: 0;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow: hidden;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
</style>
